/**
 * CharacterIllustration component — shared types and Zod prop schema.
 *
 * Renders named static character illustrations from the illustrations
 * element registry. `appearance` selects how the artwork is drawn: absent for
 * the original colour artwork, 'mono' for the line-art rendering, or a tint
 * token name for the colour artwork with its accent recoloured at runtime.
 * Distinct from the Lottie-based AnimatedIllustration component: that one
 * plays animations; this one renders static SVG artwork.
 *
 * Source of truth: Pangea Galapagos UI Design System — "Character illustration"
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import {
  characterIllustrationDimensions,
  characterIllustrationNameSet,
  getCharacterIllustrationAccent,
  isIllustrationTint,
  illustrationTintNames,
} from '../../elements/illustrations';
import type {
  CharacterIllustrationName,
  CharacterIllustrationVariant,
  IllustrationTint,
} from '../../elements/illustrations';

/** Compatibility export; the headless package owns this contract. */
export * from '@galapagos/catalog-schema/components/CharacterIllustration/CharacterIllustration';
import type {
  CharacterIllustrationAppearance,
  CharacterIllustrationSize,
} from '@galapagos/catalog-schema/components/CharacterIllustration/CharacterIllustration';
/** The two props that select a rendering, as any caller may supply them. */
export interface CharacterIllustrationAppearanceProps {
  appearance?: CharacterIllustrationAppearance;
  colour?: boolean;
}

/** Why an illustration could not be rendered as asked. */
export type IllustrationWarningReason =
  /** The value is not `'mono'` and not a name in the closed tint set. */
  | 'unknown-appearance'
  /** The artwork has no declared accent to recolour. */
  | 'no-accent'
  /** The platform cannot recolour the artwork at all (web's <img>). */
  | 'platform-unsupported'
  /** The name is not in the illustration registry at all. */
  | 'unknown-name';

/** Which artwork to load, the tint to apply once loaded, and what was dropped. */
export interface ResolvedCharacterIllustrationAppearance {
  variant: CharacterIllustrationVariant;
  tint: IllustrationTint | null;
  /** Non-null when a tint was asked for but cannot be applied. */
  droppedTint: IllustrationWarningReason | null;
}

/** Rendered pixel width for each size variant. Full uses the parent's width. */
export const sizeToWidth: Record<Exclude<CharacterIllustrationSize, 'Full'>, number> = {
  S: 100,
  SM: 150,
  M: 200,
  L: 300,
};

/**
 * Keys already warned about, so a dropped tint reports once rather than on
 * every render.
 *
 * Keyed on illustration + reason, never on the interpolated message: the
 * message embeds the value the caller asked for, and the whole premise of the
 * fallback is that validation can be bypassed, so a message-keyed set would
 * retain one string per distinct bogus value forever — on the render path. The
 * cap is the backstop for an untyped caller varying `name` too.
 */
const warnedTintDrops = new Set<string>();
const WARNED_TINT_DROP_CAP = 128;

/** The message a dropped tint or unknown name reports. */
export function illustrationWarningMessage(
  name: string,
  reason: IllustrationWarningReason,
): string {
  if (reason === 'unknown-name') {
    return `unknown name "${name}" — rendering nothing.`;
  }
  if (reason === 'unknown-appearance') {
    return `unknown appearance on "${name}" — ignored, rendering untinted. Expected "mono" or one of: ${illustrationTintNames.join(', ')}.`;
  }
  if (reason === 'no-accent') {
    return `illustration "${name}" has no tintable accent, so the requested tint cannot apply — rendering the default.`;
  }
  return `a tint on "${name}" recolours the artwork at runtime, which web cannot do — the asset renders through an <img>, whose fills CSS cannot reach. Rendering the untinted artwork. Closing the gap needs inlined markup, build-time tinted assets, or artwork re-authored with currentColor.`;
}

/**
 * Report a dropped tint once per illustration + reason.
 *
 * Called from an effect, never during render: this mutates module state and
 * writes to the console, and React Compiler treats a side effect in a component
 * body as a reason to skip that component for auto-memoisation.
 */
export function warnIllustrationOnce(name: string, drop: IllustrationWarningReason): void {
  if (process.env.NODE_ENV === 'production') return;
  const key = `${name}:${drop}`;
  // Bailing at the cap, rather than warning without recording, is the whole
  // point: storing nothing while still warning inverts the guarantee into
  // once-per-render for every key past the cap. Going silent loses a message;
  // going unrecorded loses the property.
  if (warnedTintDrops.has(key) || warnedTintDrops.size >= WARNED_TINT_DROP_CAP) return;
  warnedTintDrops.add(key);
  console.warn(`CharacterIllustration: ${illustrationWarningMessage(name, drop)}`);
}

/**
 * The single choke point mapping both props onto the registry's variant axis
 * plus an optional tint, so no renderer re-derives the mapping and the
 * deprecated alias lives in exactly one place.
 *
 * `appearance` wins whenever it is supplied: a tint implies the colour artwork,
 * so `{ appearance: <tint>, colour: false }` resolves the tinted colour
 * artwork rather than a mono file that has no accent to recolour.
 *
 * A tint this illustration cannot take — a name outside the closed set, or an
 * artwork with no accent to recolour — resolves to the default rendering and is
 * reported in `droppedTint`. Both are authoring mistakes the schema already
 * rejects, so reaching here means something bypassed validation (untyped JS, or
 * a server-authored document); degrading to the default keeps a content error
 * from taking down the screen around it, and the caller reports the drop so the
 * author still learns their tint did not apply.
 *
 * Pure: it decides, it does not warn. Reporting is the renderer's job, in an
 * effect.
 */
export function resolveAppearance(
  name: CharacterIllustrationName,
  { appearance, colour }: CharacterIllustrationAppearanceProps,
): ResolvedCharacterIllustrationAppearance {
  if (appearance === 'mono') {
    return { variant: 'mono', tint: null, droppedTint: null };
  }
  if (appearance !== undefined) {
    const fallbackVariant = colour === false ? 'mono' : 'colour';
    if (!isIllustrationTint(appearance)) {
      return { variant: fallbackVariant, tint: null, droppedTint: 'unknown-appearance' };
    }
    if (getCharacterIllustrationAccent(name) === null) {
      return { variant: 'colour', tint: null, droppedTint: 'no-accent' };
    }
    return { variant: 'colour', tint: appearance, droppedTint: null };
  }
  return { variant: colour === false ? 'mono' : 'colour', tint: null, droppedTint: null };
}

/**
 * Whether this name is in the registry at all.
 *
 * The registries index by name and throw on a miss, so both renderers check
 * this before touching them. Without it the elements layer's tolerance of an
 * unregistered name is contradicted two lines later by a TypeError from the
 * asset or dimension lookup — the schema rejects such a name, but the fallback
 * path exists precisely for values that bypassed validation.
 */
export function isRegisteredIllustration(name: string): name is CharacterIllustrationName {
  return characterIllustrationNameSet.has(name);
}

/** Intrinsic width/height ratio, used to derive height from a rendered width. */
export function getIntrinsicAspectRatio(name: CharacterIllustrationName): number {
  const { width, height } = characterIllustrationDimensions[name];
  return width / height;
}
